import { MlTextRecord } from '../../service/ml-description.service';
import {
  ProductCardButtonStyle,
  ProductCardCurrencySymbolPosition,
  ProductCardFontSize,
  ProductCardFontWeight,
  ProductCardImageFit,
  ProductCardImageRatio,
  ProductCardLayoutType,
  ProductCardMaxLines,
} from './product-card-display.model';

export const PRODUCT_CARD_LAYOUT_KEY = 'product-cards';

export const PRODUCT_CARD_LAYOUT_TYPES = ['vertical', 'horizontal', 'line'] as const;

export type ProductCardElementId =
  | 'wishlist'
  | 'image'
  | 'title'
  | 'sku'
  | 'description'
  | 'availability'
  | 'price'
  | 'addToCart'
  | 'compareButton';

export const DEFAULT_PRODUCT_CARD_ELEMENT_ORDER: ProductCardElementId[] = [
  'wishlist',
  'image',
  'title',
  'sku',
  'description',
  'availability',
  'price',
  'addToCart',
  'compareButton',
];

/** Card chrome elements that keep their default slot when sections are reordered. */
export const FIXED_PRODUCT_CARD_ELEMENT_IDS = new Set<ProductCardElementId>([
  'wishlist',
  'image',
  'addToCart',
  'compareButton',
]);

export function isProductCardElementReorderable(elementId: ProductCardElementId): boolean {
  return !FIXED_PRODUCT_CARD_ELEMENT_IDS.has(elementId);
}

/** Merges saved reorderable sequence with fixed elements at their default positions. */
export function resolveProductCardElementOrder(
  savedOrder?: ProductCardElementId[] | null
): ProductCardElementId[] {
  const reorderableFromSaved: ProductCardElementId[] = [];

  for (const id of savedOrder ?? []) {
    if (
      isProductCardElementReorderable(id) &&
      DEFAULT_PRODUCT_CARD_ELEMENT_ORDER.includes(id) &&
      !reorderableFromSaved.includes(id)
    ) {
      reorderableFromSaved.push(id);
    }
  }

  for (const id of DEFAULT_PRODUCT_CARD_ELEMENT_ORDER) {
    if (isProductCardElementReorderable(id) && !reorderableFromSaved.includes(id)) {
      reorderableFromSaved.push(id);
    }
  }

  const reordered: ProductCardElementId[] = [];
  let reorderableInserted = false;

  for (const id of DEFAULT_PRODUCT_CARD_ELEMENT_ORDER) {
    if (isProductCardElementReorderable(id)) {
      if (!reorderableInserted) {
        reordered.push(...reorderableFromSaved);
        reorderableInserted = true;
      }
      continue;
    }

    reordered.push(id);
  }

  return reordered;
}

/** Reorderable card body elements in display order (excludes fixed chrome). */
export function resolveProductCardContentElementOrder(
  savedOrder?: ProductCardElementId[] | null
): ProductCardElementId[] {
  return resolveProductCardElementOrder(savedOrder).filter((id) => isProductCardElementReorderable(id));
}

/** Settings for a single card variant (vertical grid, horizontal list, or line list). */
export interface ProductCardLayoutSavedConfig {
  cornerRadius?: number;
  elementOrder?: ProductCardElementId[];
  wishlistEnabled?: boolean;
  wishlistVisible?: boolean;
  imageRatio?: ProductCardImageRatio;
  imageFit?: ProductCardImageFit;
  imageVisible?: boolean;
  titleSource?: string;
  titleFontSize?: ProductCardFontSize;
  titleFontWeight?: ProductCardFontWeight;
  titleMaxLines?: ProductCardMaxLines;
  titleVisible?: boolean;
  skuSource?: string;
  skuFontSize?: ProductCardFontSize;
  skuFontWeight?: ProductCardFontWeight;
  skuMaxLines?: ProductCardMaxLines;
  skuVisible?: boolean;
  skuTitleMl?: MlTextRecord;
  /** Legacy / API alias for {@link skuTitleMl}. */
  skuTitle?: MlTextRecord;
  descriptionSource?: string;
  descriptionFontSize?: ProductCardFontSize;
  descriptionFontWeight?: ProductCardFontWeight;
  descriptionMaxLines?: ProductCardMaxLines;
  descriptionVisible?: boolean;
  availableLabel?: MlTextRecord;
  preOrderLabel?: MlTextRecord;
  limitedLabel?: MlTextRecord;
  availabilityVisible?: boolean;
  priceTitleMl?: MlTextRecord;
  /** Legacy / API alias for {@link priceTitleMl}. */
  priceTitle?: MlTextRecord;
  priceCurrencyFormat?: string;
  symbolActivationEnabled?: boolean;
  symbolStyle?: string;
  currencySymbolPosition?: ProductCardCurrencySymbolPosition;
  priceVisible?: boolean;
  countryAssignment?: {
    selectedValue?: string[];
    assignedCountryCodes?: string[];
  };
  addToCartButtonStyle?: ProductCardButtonStyle;
  addToCartVisible?: boolean;
  compareButtonStyle?: ProductCardButtonStyle;
  compareButtonVisible?: boolean;
}

/** Root payload stored under `layoutData['product-cards']`. */
export interface ProductCardLayoutsRoot {
  /** Last card variant edited in the admin customizer. */
  activeCardType?: ProductCardLayoutType;
  /** @deprecated Legacy alias for {@link activeCardType}. */
  cardType?: ProductCardLayoutType;
  layouts?: Partial<Record<ProductCardLayoutType, ProductCardLayoutSavedConfig>>;
}

/** @deprecated Flat root shape before per-variant layouts were introduced. */
export interface ProductCardLayoutLegacyRoot extends ProductCardLayoutSavedConfig {
  cardType?: ProductCardLayoutType;
  layouts?: never;
}
